import { createSource } from '@wix/media-manager-sdk';
import { always, cond, isNil, pipe, T } from 'ramda';
import { MediaType } from '../../constants/MediaType';
import { ExternalSource, MainSource, Source } from '../../constants/Source';
import { getLocale } from '../env/getLocale';
import {
  TMMGRAdapterSettings,
  TMMGRSettings,
  TUnsplashAdapterSettings,
  TShutterstockAdapterSettings,
} from '../hoc/withMMGRSettings/withMMGRSettings.types';
import { memoize } from '../memoize';
import { MediaManagerSDKAdapter, MediaManagerSDKAnyItem } from './index';
import { SOURCE_ADAPTERS } from './sourceAdapters';
import { isInTrash, isMediaFromWixItem } from './items';

export const mmgrSettingsToAdapterSettings = memoize(
  (
    source: Source,
    {
      privateMediaRestAPI,
      privateMediaRestFiles,
      privateMediaRestVideoFiles,
      siteMediaToken,
      topLevelMediaToken,
      namespace,
      unsplashRestAPI,
      publicMediaRestAPI,
      publicMediaRestFiles,
      publicMediaRestVideoFiles,
      publicMediaRoot,
      pixAPI,
      customData,
      isSecureContext,
      tabsList,
      restrictContent,
      restrictK12,
      isEmailWithWixDomain,
      recommendedRestApi,
      recommendedPreferencesRestApi,
      recommendedTopicsListApiUrl,
      metaSiteToken,
      team,
      country,
    }: TMMGRSettings,
  ): TMMGRAdapterSettings => {
    const commonSettings = {
      apiUrl: privateMediaRestAPI,
      filesUrl: privateMediaRestFiles,
      videoFilesUrl: privateMediaRestVideoFiles,
      asyncUploadByUrl: false,
      siteMediaToken,
      metaSiteToken,
      namespace,
      uploadLimits: {
        default: Infinity,
        [MediaType.Document]: Infinity,
        [MediaType.Picture]: Infinity,
        [MediaType.VectorArt]: Infinity,
        [MediaType.Music]: Infinity,
        [MediaType.SecureMusic]: Infinity,
        [MediaType.Video]: Infinity,
        [MediaType.SecureVideo]: Infinity,
      },
    };

    const favoritesSettings = { ...commonSettings, useCache: true };
    const wixMediaSettings = {
      favoritesSettings,
      siteMediaToken,
      metaSiteToken,
      apiUrl: publicMediaRestAPI,
      filesUrl: publicMediaRestFiles,
      videoFilesUrl: publicMediaRestVideoFiles,
      wixUser: isEmailWithWixDomain,
      publicMediaRoot,
      language: getLocale(),
      restrictContent,
      restrictK12,
    };

    const unsplashSettings: TUnsplashAdapterSettings = {
      siteMediaToken,
      metaSiteToken,
      favoritesSettings,
      prefetchNextPage: true,
      apiUrl: unsplashRestAPI,
      language: getLocale(),
    };

    const recommendedHomeSettings = {
      apiUrl: recommendedRestApi,
      recommendedPreferencesApiUrl: recommendedPreferencesRestApi,
      topicsListApiUrl: recommendedTopicsListApiUrl,
    };

    switch (source) {
      case MainSource.SiteFiles:
      case MainSource.VisitorsUploads:
      case MainSource.Trash:

      case ExternalSource.WixVideo:
        return commonSettings;

      case ExternalSource.WixSite:
        return {
          ...commonSettings,
          topLevelMediaToken,
        };

      case MainSource.Boards:
        return {
          ...commonSettings,
          isTeam: !isNil(team),
        };

      case MainSource.Recommended:
        return {
          ...commonSettings,
          customData,
        };

      case MainSource.Shutterstock:
        return {
          siteMediaToken,
          metaSiteToken,
          favoritesSettings,
          prefetchNextPage: true,
          country,
        } as TShutterstockAdapterSettings;

      case MainSource.Unsplash: {
        return unsplashSettings;
      }

      case MainSource.MediaFromWix:
      case MainSource.Enterprise:
      case MainSource.Yell:
      case MainSource.NTT: {
        return { ...wixMediaSettings, prefetchNextPage: true };
      }

      case MainSource.All:
        return {
          tabsList,
          siteMediaToken,
          metaSiteToken,
          isSecureContext,
          siteFilesSettings: commonSettings,
          wixMediaSettings,
          favoritesSettings,
          unsplashSettings,
        };

      case MainSource.Home:
        return {
          siteMediaToken,
          metaSiteToken,
          siteFilesSettings: commonSettings,
          wixMediaSettings,
          favoritesSettings,
          recommendedHomeSettings,
          publicMediaRoot,
          tabsList,
        };

      case ExternalSource.Facebook:
      case ExternalSource.Dribbble:
      case ExternalSource.GoogleDrive:
      case ExternalSource.Dropbox:
      case ExternalSource.Vimeo:
      case ExternalSource.Instagram:
      case ExternalSource.Flickr:
      case ExternalSource.GooglePhotos:
      case ExternalSource.DeviantArt:
        return {
          apiUrl: pixAPI,
          siteMediaToken,
          metaSiteToken,
          namespace,
          siteFilesSettings: commonSettings,
        };

      default:
        return {
          siteMediaToken,
          metaSiteToken,
          namespace,
          siteFilesSettings: commonSettings,
        };
    }
  },
);

export const getAdapterWithAdapterSettings = memoize(
  (source: Source, adapterSettings: TMMGRAdapterSettings): MediaManagerSDKAdapter =>
    SOURCE_ADAPTERS[source] ? createSource(SOURCE_ADAPTERS[source], adapterSettings) : null,
  JSON.stringify.bind(JSON),
);

/**
 * @deprecated as we are moving from HOCs to hooks, please use `useAdapter` if possible
 */
export const getAdapter = (source: Source, mmgrSettings: TMMGRSettings): MediaManagerSDKAdapter =>
  getAdapterWithAdapterSettings(source, mmgrSettingsToAdapterSettings(source, mmgrSettings));

export const getItemSourceSafe = cond<MediaManagerSDKAnyItem, Source>([
  [isInTrash, always(MainSource.Trash)],
  [isMediaFromWixItem, always(MainSource.MediaFromWix)],
  [T, always(MainSource.SiteFiles)],
]);

export const getItemAdapter = (item: MediaManagerSDKAnyItem, mmgrSettings: TMMGRSettings): MediaManagerSDKAdapter =>
  pipe(getItemSourceSafe, (source) => getAdapter(source, mmgrSettings))(item);
